/** Generic overlay attribute resolution for any entity surface (person, team, …). */

export type OverlayAttributePopulation = "empty" | "partial" | "full";

export type OverlayAttributePresence = "present" | "missing";

export type OverlayAttributeResolvedValue = {
  key: string;
  displayValue: string | null;
  presence: OverlayAttributePresence;
};

export type OverlayAttributeBadgeModel = {
  key: string;
  name: string;
  label: string;
  presence: OverlayAttributePresence;
};

export type OverlayCatalogOption = {
  value: string;
  label: string;
};

export type OverlayAttributeSelection = {
  value: string;
  label?: string | null;
  isSelected?: boolean | null | undefined;
};

export type OverlayAttributeRegistry<Entity> = {
  resolveValue: (entity: Entity, key: string) => string | null;
};

export const formatTrimmedOverlayValue = (value: unknown): string | null => {
  if (value == null) {
    return null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

export const resolveOverlayAttributeValue = <Entity>(
  entity: Entity | null | undefined,
  key: string,
  registry: OverlayAttributeRegistry<Entity>
): OverlayAttributeResolvedValue => {
  if (!entity) {
    return { key, displayValue: null, presence: "missing" };
  }
  const displayValue = registry.resolveValue(entity, key);
  return {
    key,
    displayValue,
    presence: displayValue ? "present" : "missing",
  };
};

const countEntitiesWithOverlayValue = <Entity>(
  key: string,
  entities: Entity[],
  registry: OverlayAttributeRegistry<Entity>
): number =>
  entities.reduce((count, entity) => {
    const value = registry.resolveValue(entity, key);
    return value ? count + 1 : count;
  }, 0);

/** Canvas-wide coverage for a key across visible entities. */
export const classifyOverlayAttributePopulation = <Entity>(
  key: string,
  entities: Entity[],
  registry: OverlayAttributeRegistry<Entity>
): OverlayAttributePopulation => {
  if (entities.length === 0) {
    return "empty";
  }
  const populatedCount = countEntitiesWithOverlayValue(key, entities, registry);
  if (populatedCount === 0) {
    return "empty";
  }
  if (populatedCount === entities.length) {
    return "full";
  }
  return "partial";
};

const overlayAttributeKeyHasValueOnAnyEntity = <Entity>(
  key: string,
  entities: Entity[],
  registry: OverlayAttributeRegistry<Entity>
): boolean =>
  classifyOverlayAttributePopulation(key, entities, registry) !== "empty";

export const filterOverlayCatalogOptionsByVisibleEntities = <Entity>({
  options,
  entities,
  registry,
  alwaysIncludeKeys = [],
  hideEmptyOptions = false,
}: {
  options: OverlayCatalogOption[];
  entities: Entity[];
  registry: OverlayAttributeRegistry<Entity>;
  alwaysIncludeKeys?: string[];
  hideEmptyOptions?: boolean;
}): OverlayCatalogOption[] => {
  if (!hideEmptyOptions) {
    return options;
  }
  if (entities.length === 0) {
    return options.filter((option) => alwaysIncludeKeys.includes(option.value));
  }
  const alwaysInclude = new Set(alwaysIncludeKeys);
  return options.filter((option) => {
    if (alwaysInclude.has(option.value)) {
      return true;
    }
    return overlayAttributeKeyHasValueOnAnyEntity(
      option.value,
      entities,
      registry
    );
  });
};

export const buildOverlayBadgesForEntity = <Entity>({
  selections,
  entity,
  registry,
  getAttributeName,
  isKeyAllowed = () => true,
  missingLabel = "Missing",
}: {
  selections: OverlayAttributeSelection[];
  entity: Entity | null | undefined;
  registry: OverlayAttributeRegistry<Entity>;
  getAttributeName: (key: string, selectionLabel?: string | null) => string;
  isKeyAllowed?: (key: string) => boolean;
  missingLabel?: string;
}): OverlayAttributeBadgeModel[] => {
  const badges: OverlayAttributeBadgeModel[] = [];

  for (const selection of selections) {
    if (!selection?.isSelected || !selection.value) {
      continue;
    }
    if (!isKeyAllowed(selection.value)) {
      continue;
    }

    const name = getAttributeName(selection.value, selection.label);
    const resolved = resolveOverlayAttributeValue(
      entity,
      selection.value,
      registry
    );

    if (resolved.presence === "present") {
      badges.push({
        key: selection.value,
        name,
        label: resolved.displayValue!,
        presence: "present",
      });
      continue;
    }

    badges.push({
      key: selection.value,
      name,
      label: missingLabel,
      presence: "missing",
    });
  }

  return badges;
};
